import React, { useEffect, useMemo, useState } from 'react';
import { FaLayerGroup, FaPlus, FaRedo, FaRupeeSign, FaTrashAlt } from 'react-icons/fa';
import api from '../lib/api';
import './CSS/PlatformFinance.css';

function readRole() {
  const raw = localStorage.getItem('role');
  if (!raw) return '';
  try {
    return String(JSON.parse(raw)).trim().toLowerCase();
  } catch {
    return String(raw).trim().toLowerCase();
  }
}

function formatCurrency(value) {
  return `Rs ${Number(value || 0).toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function PlatformCompanyBooks() {
  const token = localStorage.getItem('token');
  const role = readRole();
  const isSuperAdmin = role === 'super_admin';

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [categoryForm, setCategoryForm] = useState({ name: '', code: '' });
  const [expenseForm, setExpenseForm] = useState({
    expense_date: new Date().toISOString().slice(0, 10),
    category_id: '',
    amount: '',
    payment_mode: 'bank_transfer',
    payment_reference: '',
    description: '',
  });
  const [ruleForm, setRuleForm] = useState({
    name: '',
    category_id: '',
    amount: '',
    frequency: 'monthly',
    start_date: new Date().toISOString().slice(0, 10),
    next_run_date: new Date().toISOString().slice(0, 10),
    payment_mode: 'bank_transfer',
    notes: '',
    auto_post: true,
  });

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAll();
    }
  }, [isSuperAdmin]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [summaryRes, categoryRes, expenseRes, ruleRes] = await Promise.all([
        api.get('/api/platform-finance/company-books/summary', authHeaders),
        api.get('/api/platform-finance/company-books/categories', authHeaders),
        api.get('/api/platform-finance/company-books/expenses', authHeaders),
        api.get('/api/platform-finance/company-books/recurring-rules', authHeaders),
      ]);

      setSummary(summaryRes.data || null);
      setCategories(categoryRes.data || []);
      setExpenses(expenseRes.data || []);
      setRules(ruleRes.data || []);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to load company books');
    } finally {
      setLoading(false);
    }
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.name.trim()) return;
    try {
      setSaving(true);
      await api.post('/api/platform-finance/company-books/categories', categoryForm, authHeaders);
      setCategoryForm({ name: '', code: '' });
      await fetchAll();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const saveExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.expense_date || !expenseForm.category_id || !expenseForm.amount) return;
    try {
      setSaving(true);
      await api.post('/api/platform-finance/company-books/expenses', expenseForm, authHeaders);
      setExpenseForm((prev) => ({
        ...prev,
        amount: '',
        payment_reference: '',
        description: '',
      }));
      await fetchAll();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const saveRule = async (e) => {
    e.preventDefault();
    if (!ruleForm.name.trim() || !ruleForm.category_id || !ruleForm.amount) return;
    try {
      setSaving(true);
      await api.post('/api/platform-finance/company-books/recurring-rules', ruleForm, authHeaders);
      setRuleForm((prev) => ({
        ...prev,
        name: '',
        amount: '',
        notes: '',
      }));
      await fetchAll();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to save recurring rule');
    } finally {
      setSaving(false);
    }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Delete this company expense?')) return;
    try {
      await api.delete(`/api/platform-finance/company-books/expenses/${id}`, authHeaders);
      await fetchAll();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to delete expense');
    }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this recurring rule?')) return;
    try {
      await api.delete(`/api/platform-finance/company-books/recurring-rules/${id}`, authHeaders);
      await fetchAll();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to delete recurring rule');
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="platform-finance-page">
        <div className="platform-finance-surface">
          <h2>Super admin only</h2>
          <p>This company books workspace is only available for the main platform owner account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="platform-finance-page">
      <section className="platform-finance-hero">
        <div>
          <div className="platform-finance-chip">Company Books</div>
          <h1>Track FuelFlow company expenses outside tenant books.</h1>
          <p>
            Separate your own company accounting from petrol bunk tenant data, including recurring software,
            hosting, payroll, travel, and one-off platform costs.
          </p>
        </div>

        <button className="platform-finance-btn secondary" onClick={fetchAll}>
          <FaRedo />
          <span>{loading ? 'Refreshing...' : 'Refresh'}</span>
        </button>
      </section>

      <section className="platform-finance-summary-grid">
        <StatCard label="This Month" value={formatCurrency(summary?.month_total)} hint="Current company spend" icon={<FaRupeeSign />} />
        <StatCard label="Recurring Spend" value={formatCurrency(summary?.recurring_total)} hint="Auto-posted or recurring-linked" icon={<FaLayerGroup />} />
        <StatCard label="Categories" value={summary?.active_categories || 0} hint="Active book heads" icon={<FaLayerGroup />} />
        <StatCard label="Last Expense" value={summary?.last_expense?.category_name || '-'} hint={summary?.last_expense ? `${formatCurrency(summary.last_expense.amount)} on ${formatDate(summary.last_expense.expense_date)}` : 'No company expense yet'} icon={<FaRupeeSign />} />
      </section>

      <section className="platform-finance-grid two">
        <div className="platform-finance-surface">
          <div className="platform-finance-section-head">
            <div>
              <div className="platform-finance-kicker">Book Setup</div>
              <h3>Add company expense category</h3>
            </div>
          </div>
          <form className="platform-finance-form" onSubmit={saveCategory}>
            <input
              value={categoryForm.name}
              onChange={(e) => setCategoryForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Category name"
            />
            <input
              value={categoryForm.code}
              onChange={(e) => setCategoryForm((prev) => ({ ...prev, code: e.target.value }))}
              placeholder="Code"
            />
            <button className="platform-finance-btn primary" type="submit" disabled={saving}>
              <FaPlus />
              <span>Add Category</span>
            </button>
          </form>

          <div className="platform-finance-chip-list">
            {categories.map((category) => (
              <span key={category.id} className="platform-finance-chip-pill">
                {category.name}
              </span>
            ))}
          </div>
        </div>

        <div className="platform-finance-surface">
          <div className="platform-finance-section-head">
            <div>
              <div className="platform-finance-kicker">One-off or manual</div>
              <h3>Add non-recurring expense</h3>
            </div>
          </div>
          <form className="platform-finance-form grid-2" onSubmit={saveExpense}>
            <input
              type="date"
              value={expenseForm.expense_date}
              onChange={(e) => setExpenseForm((prev) => ({ ...prev, expense_date: e.target.value }))}
            />
            <select
              value={expenseForm.category_id}
              onChange={(e) => setExpenseForm((prev) => ({ ...prev, category_id: e.target.value }))}
            >
              <option value="">Select category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
            <input
              type="number"
              step="0.01"
              value={expenseForm.amount}
              onChange={(e) => setExpenseForm((prev) => ({ ...prev, amount: e.target.value }))}
              placeholder="Amount"
            />
            <select
              value={expenseForm.payment_mode}
              onChange={(e) => setExpenseForm((prev) => ({ ...prev, payment_mode: e.target.value }))}
            >
              <option value="bank_transfer">Bank Transfer</option>
              <option value="upi">UPI</option>
              <option value="card">Card</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
            <input
              value={expenseForm.payment_reference}
              onChange={(e) => setExpenseForm((prev) => ({ ...prev, payment_reference: e.target.value }))}
              placeholder="Payment reference"
            />
            <input
              value={expenseForm.description}
              onChange={(e) => setExpenseForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Description"
            />
            <button className="platform-finance-btn primary full" type="submit" disabled={saving}>
              <FaPlus />
              <span>Add Expense</span>
            </button>
          </form>
        </div>
      </section>

      <section className="platform-finance-surface">
        <div className="platform-finance-section-head">
          <div>
            <div className="platform-finance-kicker">Recurring</div>
            <h3>Set auto-posting rules for company books</h3>
          </div>
        </div>

        <form className="platform-finance-form grid-3" onSubmit={saveRule}>
          <input
            value={ruleForm.name}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Rule name"
          />
          <select
            value={ruleForm.category_id}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, category_id: e.target.value }))}
          >
            <option value="">Select category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </select>
          <input
            type="number"
            step="0.01"
            value={ruleForm.amount}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, amount: e.target.value }))}
            placeholder="Amount"
          />
          <select
            value={ruleForm.frequency}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, frequency: e.target.value }))}
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
            <option value="weekly">Weekly</option>
            <option value="daily">Daily</option>
          </select>
          <input
            type="date"
            value={ruleForm.start_date}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, start_date: e.target.value }))}
          />
          <input
            type="date"
            value={ruleForm.next_run_date}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, next_run_date: e.target.value }))}
          />
          <select
            value={ruleForm.payment_mode}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, payment_mode: e.target.value }))}
          >
            <option value="bank_transfer">Bank Transfer</option>
            <option value="upi">UPI</option>
            <option value="card">Card</option>
            <option value="cash">Cash</option>
            <option value="other">Other</option>
          </select>
          <input
            value={ruleForm.notes}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, notes: e.target.value }))}
            placeholder="Notes"
          />
          <button className="platform-finance-btn primary" type="submit" disabled={saving}>
            <FaPlus />
            <span>Add Rule</span>
          </button>
        </form>
      </section>

      <section className="platform-finance-grid two">
        <div className="platform-finance-surface">
          <div className="platform-finance-section-head">
            <div>
              <div className="platform-finance-kicker">Ledger</div>
              <h3>Recent company expenses</h3>
            </div>
          </div>

          <div className="platform-finance-table-wrap">
            <table className="platform-finance-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Amount</th>
                  <th>Mode</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {expenses.slice(0, 12).map((expense) => (
                  <tr key={expense.id}>
                    <td>{formatDate(expense.expense_date)}</td>
                    <td>{expense.category_name || '-'}</td>
                    <td>{formatCurrency(expense.amount)}</td>
                    <td>{expense.payment_mode || '-'}</td>
                    <td>
                      <button className="platform-finance-icon-btn danger" onClick={() => deleteExpense(expense.id)}>
                        <FaTrashAlt />
                      </button>
                    </td>
                  </tr>
                ))}
                {!expenses.length && (
                  <tr>
                    <td colSpan="5">No company expenses yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="platform-finance-surface">
          <div className="platform-finance-section-head">
            <div>
              <div className="platform-finance-kicker">Recurring Queue</div>
              <h3>Active platform recurring rules</h3>
            </div>
          </div>

          <div className="platform-finance-table-wrap">
            <table className="platform-finance-table">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Next Run</th>
                  <th>Amount</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id}>
                    <td>{rule.name}</td>
                    <td>{formatDate(rule.next_run_date)}</td>
                    <td>{formatCurrency(rule.amount)}</td>
                    <td>
                      <button className="platform-finance-icon-btn danger" onClick={() => deleteRule(rule.id)}>
                        <FaTrashAlt />
                      </button>
                    </td>
                  </tr>
                ))}
                {!rules.length && (
                  <tr>
                    <td colSpan="4">No recurring rules yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, hint, icon }) {
  return (
    <div className="platform-finance-stat">
      <div className="platform-finance-stat-icon">{icon}</div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{hint}</small>
      </div>
    </div>
  );
}
